import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function ResetPassword() {
  const { t } = useTranslation('app');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const validatePasswordStrength = (pwd) => {
    if (pwd.length < 8) return 'weak';
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /\d/.test(pwd);
    const hasSpecial = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(pwd);

    const strengthScore = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (strengthScore < 2) return 'weak';
    if (strengthScore === 2) return 'fair';
    if (strengthScore === 3) return 'good';
    return 'strong';
  };

  const getPasswordStrengthColor = () => {
    const colors = {
      weak: 'bg-error',
      fair: 'bg-warning',
      good: 'bg-info',
      strong: 'bg-success',
    };
    return colors[passwordStrength] || 'bg-outline';
  };

  const getPasswordStrengthText = () => {
    const texts = {
      weak: t('auth.resetPassword.weak'),
      fair: t('auth.resetPassword.fair'),
      good: t('auth.resetPassword.good'),
      strong: t('auth.resetPassword.strong'),
    };
    return texts[passwordStrength] || '';
  };

  useEffect(() => {
    if (password) {
      setPasswordStrength(validatePasswordStrength(password));
    } else {
      setPasswordStrength('');
    }
  }, [password]);

  useEffect(() => {
    setPasswordsMatch(password === confirmPassword || confirmPassword === '');
  }, [password, confirmPassword]);

  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
        <div className="text-center">
          <h1 className="text-headline-lg text-error mb-4">{t('auth.resetPassword.invalidToken')}</h1>
          <p className="text-body-md text-on-background/60 mb-6">{t('auth.resetPassword.invalidTokenDesc')}</p>
          <Link
            to="/forgot-password"
            className="inline-block px-6 py-2 bg-primary text-on-primary rounded-lg hover:opacity-90 transition-opacity"
          >
            {t('auth.resetPassword.requestNewLink')}
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.resetPassword.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const contentType = response.headers.get('content-type');
      const data = contentType?.includes('application/json')
        ? await response.json()
        : null;

      if (!response.ok) {
        throw new Error(data?.error ?? `Request failed (${response.status}). Please try again.`);
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile">
        <div className="w-full max-w-md text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-4">
              <span className="material-symbols-outlined text-success text-headline-lg">check_circle</span>
            </div>
            <h1 className="text-headline-lg text-on-background mb-3">{t('auth.resetPassword.resetSuccess')}</h1>
            <p className="text-body-md text-on-background/60">
              {t('auth.resetPassword.resetSuccessDesc')}
            </p>
          </div>

          <p className="text-body-sm text-on-background/60">{t('auth.resetPassword.redirecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile">
      <div className="w-full max-w-md">
        <h1 className="text-headline-lg text-on-background mb-2 text-center">{t('auth.resetPassword.title')}</h1>
        <p className="text-body-md text-on-background/60 text-center mb-8">{t('auth.resetPassword.subtitle')}</p>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-body-sm text-on-background mb-1">
              {t('auth.resetPassword.newPassword')}
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-2 pr-10 bg-surface border rounded-lg text-on-surface text-body-sm focus:outline-none ${
                  password && !validatePasswordStrength(password).includes('weak')
                    ? 'border-success focus:border-success'
                    : 'border-outline focus:border-primary'
                }`}
                placeholder="••••••••"
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-background/60 hover:text-on-background transition-colors"
                title={showPassword ? t('auth.resetPassword.hidePassword') : t('auth.resetPassword.showPassword')}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {password && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-2 flex-1 rounded ${getPasswordStrengthColor()}`}></div>
                  <span className="text-label-sm text-on-background/60">{getPasswordStrengthText()}</span>
                </div>
                <p className="text-label-sm text-on-background/50">
                  {t('auth.resetPassword.passwordRequirements')}
                </p>
              </div>
            )}
            {!password && (
              <p className="text-label-sm text-on-background/50 mt-1">{t('auth.resetPassword.minCharacters')}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-body-sm text-on-background mb-1">
              {t('auth.resetPassword.confirmPassword')}
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full px-4 py-2 pr-10 bg-surface border rounded-lg text-on-surface text-body-sm focus:outline-none ${
                  confirmPassword && passwordsMatch
                    ? 'border-success focus:border-success'
                    : confirmPassword && !passwordsMatch
                    ? 'border-error focus:border-error'
                    : 'border-outline focus:border-primary'
                }`}
                placeholder="••••••••"
                minLength="8"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-on-background/60 hover:text-on-background transition-colors"
                title={showConfirmPassword ? t('auth.resetPassword.hidePassword') : t('auth.resetPassword.showPassword')}
              >
                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {confirmPassword && (
              <p className={`text-label-sm mt-1 ${passwordsMatch ? 'text-success' : 'text-error'}`}>
                {passwordsMatch ? t('auth.resetPassword.passwordsMatch') : t('auth.resetPassword.passwordsDontMatch')}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !passwordsMatch || !passwordStrength || passwordStrength === 'weak'}
            className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 text-body-md"
            title={
              !passwordsMatch
                ? t('auth.resetPassword.passwordMismatch')
                : !passwordStrength
                ? t('auth.resetPassword.passwordTooShort')
                : passwordStrength === 'weak'
                ? t('auth.resetPassword.passwordTooShort')
                : ''
            }
          >
            {loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.resetButton')}
          </button>
        </form>

        <p className="text-body-sm text-on-background/60 text-center mt-6">
          <Link to="/login" className="text-primary font-medium hover:underline">
            {t('auth.forgotPassword.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}
